import { FunctionComponent, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../../../components/common/Button/Button';
import { Confetti, ConfettiHandle } from '../../../components/common/Confetti/Confetti';
import { PageButtons } from '../../../components/common/PageButtons/PageButtons';
import { PageContainer } from '../../../components/common/PageContainer/PageContainer';
import { FadeInImage } from '../../../components/utils/FadeInImage/FadeInImage';
import { useBooking } from '../../../contexts/BookingProvider';
import { useBookingParams } from '../../../hooks/useBookingParams';
import { CountryInfo, useCountries } from '../../../hooks/useCountries';
import { Stage } from '../../../model/stage';
import classes from './ConfirmedBooking.module.scss';
import ImageLeo from './leo.png';

/**
 * The booking stage shown when the booking has been confirmed for the first time.
 */
export const ConfirmedBooking: FunctionComponent = () => {
    const booking = useBooking();
    const { currentCountry, getCountryByCulture } = useCountries();
    const { openStage } = useBookingParams();
    const [searchParams] = useSearchParams();

    const confetti = useRef<ConfettiHandle | null>(null);

    const dealsPurchased = searchParams.get('dealsPurchased') !== null;
    const fromOverview = searchParams.get('fromOverview') !== null;

    const handleImageLoaded = () => {
        setTimeout(() => {
            confetti.current?.launch({ spread: 90, particleCount: 150 });
        }, 300);
    };

    const handleInviteButtonClick = () => {
        openStage(Stage.EditInvitation, booking.details, true);
    };

    const handleOverviewButtonClick = () => {
        openStage(Stage.BookingOverview, booking.details, true);
    };

    const getMyAccountUrl = () => {
        let countryInfo: CountryInfo = currentCountry;
        if (booking.details) {
            countryInfo = getCountryByCulture(booking.details.country.culture) ?? currentCountry;
        }

        return countryInfo.urlProfile + '/offers';
    };

    const handleMyAccountButtonClick = () => {
        window.location.href = getMyAccountUrl();
    };

    return (
        <>
            <Confetti ref={confetti} />

            <PageContainer>
                <div className={classes.container}>
                    <FadeInImage className={classes.image} src={ImageLeo} onLoad={handleImageLoaded} />

                    {fromOverview ? (
                        <ConfirmedFromOverview
                            onMyAccountButtonClick={handleMyAccountButtonClick}
                            onOverviewButtonClick={handleOverviewButtonClick}
                        />
                    ) : (
                        <ConfirmedFirstTime
                            canChangeGuests={booking.details?.canChangeGuests ?? false}
                            dealsPurchased={dealsPurchased}
                            myAccountUrl={getMyAccountUrl()}
                            onInviteButtonClick={handleInviteButtonClick}
                            onOverviewButtonClick={handleOverviewButtonClick}
                        />
                    )}
                </div>
            </PageContainer>
        </>
    );
};

type ConfirmedFirstTimeProps = {
    canChangeGuests: boolean;
    dealsPurchased: boolean;
    myAccountUrl: string;
    onInviteButtonClick: () => void;
    onOverviewButtonClick: () => void;
};

const ConfirmedFirstTime: FunctionComponent<ConfirmedFirstTimeProps> = ({
    canChangeGuests,
    dealsPurchased,
    myAccountUrl,
    onInviteButtonClick,
    onOverviewButtonClick,
}) => {
    return (
        <>
            <h1 className={classes.title}>
                <FormattedMessage id="confirmed.title" defaultMessage="Nu är ditt kalas bokat!" />
            </h1>

            <p className={classes.text}>
                {canChangeGuests ? (
                    <FormattedMessage
                        id="confirmed.text-invite"
                        defaultMessage="Nu kan du designa ditt inbjudningskort och bjuda in gäster!"
                    />
                ) : (
                    <FormattedMessage
                        id="confirmed.text-no-invite"
                        defaultMessage="På översiktssidan kan du se en summering av ditt kalas!"
                    />
                )}{' '}
                {dealsPurchased && (
                    <FormattedMessage
                        id="confirmed.text-deals-purchased"
                        defaultMessage="Dina kuponger för party deals finns under {accountLink}"
                        values={{
                            accountLink: (
                                <a href={myAccountUrl}>
                                    <FormattedMessage id="confirmed.my-account-link" defaultMessage="Mitt konto" />
                                </a>
                            ),
                        }}
                    />
                )}
            </p>

            <PageButtons>
                {canChangeGuests && (
                    <Button color="minionYellow" onClick={onInviteButtonClick}>
                        <FormattedMessage id="confirmed.button-invite" defaultMessage="Bjud in gäster" />
                    </Button>
                )}

                {canChangeGuests ? (
                    <Button color="transparent" onClick={onOverviewButtonClick}>
                        <FormattedMessage id="confirmed.button-skip" defaultMessage="Hoppa över" />
                    </Button>
                ) : (
                    <Button color="minionYellow" onClick={onOverviewButtonClick}>
                        <FormattedMessage id="confirmed.button-overview" defaultMessage="Fortsätt till översikt" />
                    </Button>
                )}
            </PageButtons>
        </>
    );
};

type ConfirmedFromOverviewProps = {
    onOverviewButtonClick: () => void;
    onMyAccountButtonClick: () => void;
};

const ConfirmedFromOverview: FunctionComponent<ConfirmedFromOverviewProps> = ({ onOverviewButtonClick, onMyAccountButtonClick }) => {
    return (
        <>
            <h1 className={classes.title}>
                <FormattedMessage id="confirmed.title-from-overview" defaultMessage="Klart - dina party deals är tillagda!" />
            </h1>

            <p className={classes.text}>
                <FormattedMessage
                    id="confirmed.text-from-overview"
                    defaultMessage='Kupongerna finns nu på "Mitt konto" och löses in i kassan vid besöket.'
                />
            </p>

            <PageButtons>
                <Button color="minionYellow" onClick={onMyAccountButtonClick}>
                    <FormattedMessage id="confirmed.button-your-account" defaultMessage="Till mitt konto" />
                </Button>

                <Button color="transparent" onClick={onOverviewButtonClick}>
                    <FormattedMessage id="confirmed.button-to-overview" defaultMessage="Till översikten" />
                </Button>
            </PageButtons>
        </>
    );
};
